import { ref, watch, onMounted, onUnmounted, type Ref } from 'vue'
import { MetronomeSound, type SoundOutputMode } from '@/components/sounds/MetronomeSound'

/** 调度器轮询间隔（毫秒） */
const SCHEDULE_INTERVAL_MS = 25
/** 提前排期窗口（秒） */
const LOOKAHEAD_SEC = 0.1
/** 起播 / 重锚后的首拍延迟（秒），给移动端音频硬件留出启动时间 */
const START_DELAY_SEC = 0.12
/** 音频被挡时的唤醒重试间隔（毫秒） */
const AUDIO_RETRY_INTERVAL_MS = 300
/** 连续多少帧检测到时钟未推进才判定为被系统挂起，避开 resume 瞬间的状态抖动 */
const FROZEN_FRAMES_BEFORE_DEGRADE = 3

/** 节拍推进的驱动方式：音频时钟 / 纯视觉近似 / 未运行 */
type DriveMode = 'audio' | 'visual' | null

export interface MetronomeBeats {
  /** 每小节拍数 */
  beatsPerBar: Ref<number>
}

/** 节拍时间线上的一个点：拍序 + 音频时钟时刻（秒） + 单调递增序号 */
export interface TimelinePoint {
  beat: number
  time: number
  /** 从本次播放起单调递增的拍序号，供视觉判断左右摆动方向 */
  index: number
}

/** 供视觉层插值用的节拍时间线快照 */
export interface BeatTimeline {
  /** 当前音频时钟时间（秒） */
  now: number
  /** 按时间升序的节拍点（含刚发声的最近一拍与未来已排期节拍） */
  points: TimelinePoint[]
}

/**
 * 节拍器引擎：基于 Web Audio 时钟的 lookahead 调度（"A Tale of Two Clocks"）。
 * - 发声时刻由 audioContext.currentTime 精确排期，不受主线程定时器漂移影响；
 * - 视觉拍号 currentBeat 由 rAF 对照音频时钟更新，保证音画同步；
 * - 播放中改变 tempo 平滑生效（新间隔从下一拍开始），不重置相位。
 */
export function useMetronome(tempo: Ref<number>, beats: MetronomeBeats) {
  const isPlaying = ref(false)
  const isActive = ref(false)
  const currentBeat = ref(1)
  /** 音频链路被浏览器策略挡住（有画面没声音），供 UI 提示用户 */
  const audioBlocked = ref(false)

  const sound = new MetronomeSound()
  /** 当前发声通路；media 表示节拍音走 <audio>，用于 Web Audio 被压低电量的内核 */
  const outputMode = ref<SoundOutputMode>(sound.currentOutputMode)
  let mode: DriveMode = null
  let schedulerId: number | null = null
  let rafId: number | null = null
  let retryId: number | null = null
  let frozenFrames = 0
  let flashTimeout: number | null = null
  /** 下一拍的音频时钟时刻 */
  let nextNoteTime = 0
  /** 下一拍的拍序（1-based） */
  let nextBeatNumber = 1
  /** 已排期节拍队列，供 rAF 驱动视觉 */
  const scheduled: TimelinePoint[] = []
  /** 最近一次已发声的节拍，作为时间线的可插值起点 */
  let lastPlayed: TimelinePoint | null = null
  /** 单调递增的拍序号计数器 */
  let beatIndex = 0

  function beatInterval(): number {
    return 60 / Math.max(1, tempo.value)
  }

  /** 把 lookahead 窗口内的节拍排进音频时钟 */
  function schedule() {
    const ctx = sound.audioContextOrNull
    if (!ctx) return
    // 切后台 / 息屏期间音频时钟被冻结，回到前台后 nextNoteTime 会远远落后；
    // 此时继续排期会让积压的点击全部被钳制成「同一瞬间发声」，先重锚再继续。
    if (nextNoteTime < ctx.currentTime - LOOKAHEAD_SEC) {
      resync(ctx)
      return
    }
    while (nextNoteTime < ctx.currentTime + LOOKAHEAD_SEC) {
      const isAccent = nextBeatNumber === 1
      sound.playClick(isAccent, nextNoteTime)
      scheduled.push({ beat: nextBeatNumber, time: nextNoteTime, index: beatIndex++ })
      nextNoteTime += beatInterval()
      nextBeatNumber = (nextBeatNumber % beats.beatsPerBar.value) + 1
    }
  }

  /** 以当前音频时钟为基准重排下一拍，保留拍序与摆动方向连续，避免积压连发 */
  function resync(ctx: AudioContext) {
    scheduled.length = 0
    nextNoteTime = ctx.currentTime + START_DELAY_SEC
    const prevBeat = nextBeatNumber === 1 ? beats.beatsPerBar.value : nextBeatNumber - 1
    lastPlayed = {
      beat: prevBeat,
      time: nextNoteTime - beatInterval(),
      index: Math.max(beatIndex - 1, 0),
    }
  }

  /** rAF 对照音频时钟推进视觉拍号与闪烁 */
  function updateVisuals() {
    if (sound.running) {
      frozenFrames = 0
    } else if (++frozenFrames >= FROZEN_FRAMES_BEFORE_DEGRADE) {
      // 播放中被系统挂起（锁屏 / 切后台 / 来电）：时钟不再推进，退回视觉节拍并等待唤醒
      frozenFrames = 0
      degradeToVisual()
      return
    }
    const now = sound.now
    let latest: TimelinePoint | null = null
    while (scheduled.length && scheduled[0]!.time <= now) {
      latest = scheduled.shift()!
    }
    if (latest) {
      lastPlayed = latest
      currentBeat.value = latest.beat
      flashActive()
    }
    rafId = requestAnimationFrame(updateVisuals)
  }

  /** 节拍时间线快照：发声与视觉共享同一份精确时间表，供摆锤逐帧插值。
   *  始终保证至少含“最近已发声拍 + 即将到来的下一拍”两个点，避免视觉层因 lookahead 断供而跳变。 */
  function getTimeline(): BeatTimeline | null {
    const ctx = sound.audioContextOrNull
    if (!ctx || !isPlaying.value || !lastPlayed) return null
    const points: TimelinePoint[] = [lastPlayed]
    for (const p of scheduled) points.push(p)
    // 若尚未排到下一拍，用当前 tempo 的外推时刻补齐，使当前段 [lastPlayed → 下一拍] 始终可插值
    if (nextNoteTime > points[points.length - 1]!.time) {
      points.push({ beat: nextBeatNumber, time: nextNoteTime, index: beatIndex })
    }
    if (points.length < 2) return null
    return { now: ctx.currentTime, points }
  }

  function flashActive() {
    isActive.value = true
    if (flashTimeout) clearTimeout(flashTimeout)
    flashTimeout = window.setTimeout(() => {
      isActive.value = false
      flashTimeout = null
    }, 60)
  }

  /** 只清理定时器，不动播放状态 */
  function stopScheduler() {
    if (schedulerId !== null) {
      clearInterval(schedulerId)
      clearTimeout(schedulerId)
      schedulerId = null
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    scheduled.length = 0
    lastPlayed = null
    beatIndex = 0
  }

  /** 音频时钟就绪：进入精确排期模式，从第一拍（重音）开始 */
  function startAudioDriving(ctx: AudioContext) {
    stopScheduler()
    mode = 'audio'
    isPlaying.value = true
    nextBeatNumber = 1
    beatIndex = 0
    nextNoteTime = ctx.currentTime + START_DELAY_SEC
    // 起点为“上一拍”（index -1），使首拍从另一侧极端摆向 index 0 的极端，过渡连续
    lastPlayed = { beat: beats.beatsPerBar.value, time: nextNoteTime - beatInterval(), index: -1 }
    currentBeat.value = 1
    schedulerId = window.setInterval(schedule, SCHEDULE_INTERVAL_MS)
    rafId = requestAnimationFrame(updateVisuals)
    schedule()
  }

  async function start() {
    if (isPlaying.value) return
    // 必须在任何 await 之前：iOS 只把用户手势同步调用栈内的播放请求当作合法手势
    sound.startOutputSession()
    // 懒初始化 + resume，需在用户手势的调用栈内触发，规避移动端自动播放策略
    const ctx = await sound.ensureContext()
    if (isPlaying.value) return
    if (!ctx) {
      // 环境不支持 Web Audio：降级为近似节奏的视觉节拍器
      startVisualOnly()
      return
    }
    // 媒体通路的采样要在起播前渲染好，否则头几拍会落到电平偏小的 Web Audio 直出上
    if (outputMode.value === 'media' && !sound.mediaReady) {
      outputMode.value = await sound.setOutputMode('media')
    }
    if (!sound.running) {
      // 首次手势未被浏览器认可 / 刚被系统中断：先让画面跑起来，等下一次触摸唤醒音频
      audioBlocked.value = true
      startVisualOnly()
      armAudioRetry()
      return
    }
    audioBlocked.value = false
    startAudioDriving(ctx)
  }

  /** 音频掉线时降级：保留视觉节拍，同时持续尝试把音频拉回来 */
  function degradeToVisual() {
    if (mode === 'visual') {
      armAudioRetry()
      return
    }
    audioBlocked.value = true
    stopScheduler()
    startVisualOnly()
    armAudioRetry()
  }

  /** 尝试重新唤醒音频；成功则从视觉降级切回精确的音频驱动 */
  async function tryRestoreAudio() {
    if (!isPlaying.value || sound.running) return
    if (!(await sound.resumeNow())) return
    disarmAudioRetry()
    audioBlocked.value = false
    const ctx = sound.audioContextOrNull
    if (ctx && isPlaying.value) startAudioDriving(ctx)
  }

  function handleGestureRetry() {
    // 同样要在手势同步栈内先唤醒音频会话，再进入 await
    sound.startOutputSession()
    void tryRestoreAudio()
  }

  /**
   * 移动端音频被挡时的双保险：
   * - 定时器：Android Chrome 允许非手势 resume，等硬件自己缓过来即可恢复；
   * - 全局 pointerdown：iOS 必须在用户手势内 resume，用户任意一次触摸都能救回来。
   */
  function armAudioRetry() {
    if (retryId !== null) return
    retryId = window.setInterval(() => void tryRestoreAudio(), AUDIO_RETRY_INTERVAL_MS)
    window.addEventListener('pointerdown', handleGestureRetry, { capture: true, passive: true })
  }

  function disarmAudioRetry() {
    if (retryId !== null) {
      clearInterval(retryId)
      retryId = null
    }
    window.removeEventListener('pointerdown', handleGestureRetry, { capture: true })
  }

  function handleVisibilityChange() {
    if (document.visibilityState !== 'visible' || !isPlaying.value) return
    if (sound.running) return
    // 回到前台：音频时钟多半已被冻结（iOS 还会置为非标准的 interrupted），需要重新握手
    degradeToVisual()
    void tryRestoreAudio()
  }

  /** 无 Web Audio 支持（或音频被浏览器挡住）时的降级：setTimeout 近似节奏 */
  function startVisualOnly() {
    mode = 'visual'
    isPlaying.value = true
    currentBeat.value = 1
    flashActive()
    const tick = () => {
      nextBeatNumber = (nextBeatNumber % beats.beatsPerBar.value) + 1
      currentBeat.value = nextBeatNumber
      flashActive()
      schedulerId = window.setTimeout(tick, beatInterval() * 1000)
    }
    schedulerId = window.setTimeout(tick, beatInterval() * 1000)
  }

  function stop() {
    isPlaying.value = false
    mode = null
    audioBlocked.value = false
    disarmAudioRetry()
    sound.stopOutputSession()
    // 媒体通路的延时发声靠定时器，可以连同未发的一起作废
    sound.cancelPendingOutput()
    stopScheduler()
    if (flashTimeout) {
      clearTimeout(flashTimeout)
      flashTimeout = null
    }
    isActive.value = false
    currentBeat.value = 1
    nextBeatNumber = 1
    // lookahead 窗口内已排期的点击（最多 2~3 声）无法撤销，短时内自然结束
  }

  async function toggle() {
    if (isPlaying.value) {
      stop()
    } else {
      await start()
    }
  }

  /** 立即以指定 tempo 重排下一拍（滑块拖动结束等场景）；间隔本来自实时 tempo，无需特殊处理，保留接口便于调用方表意 */
  function reschedule() {
    if (!isPlaying.value || mode !== 'audio') return
    // 调度器每轮都会读取最新 tempo，此处仅提前跑一轮，让新间隔立即生效
    schedule()
  }

  /**
   * 切换发声通路（用户开关）。渲染失败时保持原通路，以返回值为准。
   */
  async function changeOutputMode(next: SoundOutputMode) {
    outputMode.value = await sound.setOutputMode(next)
  }

  // 播放中切换拍号：若当前拍序超出新拍数则回到第一拍
  watch(beats.beatsPerBar, (n) => {
    if (nextBeatNumber > n) nextBeatNumber = 1
    if (currentBeat.value > n) currentBeat.value = 1
  })

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    disarmAudioRetry()
    stopScheduler()
    sound.cleanup()
  })

  return {
    isPlaying,
    isActive,
    currentBeat,
    audioBlocked,
    outputMode,
    start,
    stop,
    toggle,
    reschedule,
    getTimeline,
    setOutputMode: changeOutputMode,
    setVolume: (v: number) => sound.setVolume(v),
  }
}
